import React, { useState, useEffect } from 'react';
import { userAPI } from '../services/api';
import './AddressManager.css';

const AddressManager = ({ onSelect, selectedAddressId, mode = 'manage' }) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isEditing, setIsEditing] = useState(null);
  const [formData, setFormData] = useState({
    nickname: '',
    fullAddress: '',
    landmark: '',
    isDefault: false
  });
  const [success, setSuccess] = useState('');

  // Fetch addresses on mount
  useEffect(() => {
    loadAddresses();
  }, []);

  // Load addresses from API
  const loadAddresses = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getAddresses();
      if (response.success) {
        setAddresses(response.data.addresses);
      } else {
        setError('Failed to load addresses');
      }
    } catch (err) {
      setError(err.message || 'Failed to load addresses');
    } finally {
      setLoading(false);
    }
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    try {
      if (isEditing) {
        // Update existing address
        const response = await userAPI.updateAddress(isEditing, formData);
        if (response.success) {
          setAddresses(response.data.addresses);
          setSuccess('Address updated successfully');
          setIsEditing(null);
        } else {
          setError(response.message || 'Failed to update address');
        }
      } else {
        // Add new address
        const response = await userAPI.addAddress(formData);
        if (response.success) {
          setAddresses(response.data.addresses);
          setSuccess('Address added successfully');
          setIsAddingNew(false);
        } else {
          setError(response.message || 'Failed to add address');
        }
      }
      
      // Reset form
      setFormData({
        nickname: '',
        fullAddress: '',
        landmark: '',
        isDefault: false
      });
      
    } catch (err) {
      setError(err.message || 'An error occurred');
    }
  };

  // Start editing an address
  const handleEdit = (address) => {
    setIsEditing(address._id);
    setFormData({
      nickname: address.nickname,
      fullAddress: address.fullAddress,
      landmark: address.landmark || '',
      isDefault: address.isDefault
    });
    setIsAddingNew(false);
  };

  // Delete an address
  const handleDelete = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }
    
    try {
      const response = await userAPI.deleteAddress(addressId);
      if (response.success) {
        setAddresses(response.data.addresses);
        setSuccess('Address deleted successfully');
        
        // If we were editing this address, cancel editing
        if (isEditing === addressId) {
          setIsEditing(null);
          setFormData({
            nickname: '',
            fullAddress: '',
            landmark: '',
            isDefault: false
          });
        }
      } else {
        setError(response.message || 'Failed to delete address');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete address');
    }
  };

  // Set an address as default
  const handleSetDefault = async (addressId) => {
    try {
      const response = await userAPI.setDefaultAddress(addressId);
      if (response.success) {
        setAddresses(response.data.addresses);
        setSuccess('Default address set successfully');
      } else {
        setError(response.message || 'Failed to set default address');
      }
    } catch (err) {
      setError(err.message || 'Failed to set default address');
    }
  };

  // Handle selecting an address (for booking form)
  const handleAddressSelect = (address) => {
    if (onSelect && mode === 'select') {
      onSelect(address);
    }
  };

  // Cancel adding or editing
  const handleCancel = () => {
    setIsAddingNew(false);
    setIsEditing(null);
    setFormData({
      nickname: '',
      fullAddress: '',
      landmark: '',
      isDefault: false
    });
  };

  // Show the form for adding a new address
  const showAddNewForm = () => {
    setIsAddingNew(true);
    setIsEditing(null);
    setFormData({
      nickname: '',
      fullAddress: '',
      landmark: '',
      isDefault: addresses.length === 0 // Make default if first address
    });
  };

  if (loading) {
    return (
      <div className="address-manager">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading addresses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="address-manager">
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      {/* Address List */}
      {addresses.length === 0 && !isAddingNew ? (
        <div className="no-addresses">
          <p>You don't have any saved addresses yet.</p>
          <button className="btn-add-address" onClick={showAddNewForm}>
            Add Your First Address
          </button>
        </div>
      ) : (
        <div className="addresses-container">
          <div className="addresses-header">
            <h3>Your Addresses</h3>
            {!isAddingNew && !isEditing && (
              <button className="btn-add-address" onClick={showAddNewForm}>
                + Add New Address
              </button>
            )}
          </div>
          
          <div className="addresses-list">
            {addresses.map(address => (
              <div 
                key={address._id} 
                className={`address-card ${mode === 'select' && selectedAddressId === address._id ? 'selected' : ''}`}
                onClick={() => mode === 'select' && handleAddressSelect(address)}
              >
                <div className="address-header">
                  <h4>{address.nickname}</h4>
                  {address.isDefault && <span className="default-badge">Default</span>}
                </div>
                
                <div className="address-details">
                  <p>{address.fullAddress}</p>
                  {address.landmark && <p><strong>Landmark:</strong> {address.landmark}</p>}
                </div>
                
                {mode === 'manage' && (
                  <div className="address-actions">
                    <button 
                      className="btn-edit" 
                      onClick={(e) => { e.stopPropagation(); handleEdit(address); }}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn-delete" 
                      onClick={(e) => { e.stopPropagation(); handleDelete(address._id); }}
                    >
                      Delete
                    </button>
                    {!address.isDefault && (
                      <button 
                        className="btn-default" 
                        onClick={(e) => { e.stopPropagation(); handleSetDefault(address._id); }}
                      >
                        Set as Default
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Address Form */}
      {(isAddingNew || isEditing) && (
        <div className="address-form-container">
          <h3>{isEditing ? 'Edit Address' : 'Add New Address'}</h3>
          
          <form onSubmit={handleSubmit} className="address-form">
            <div className="form-group">
              <label htmlFor="nickname">Address Nickname *</label>
              <input
                type="text"
                id="nickname"
                name="nickname"
                placeholder="e.g., Home, Office, Parent's House"
                value={formData.nickname}
                onChange={handleInputChange}
                required
                className="form-input"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="fullAddress">Full Address *</label>
              <textarea
                id="fullAddress"
                name="fullAddress"
                placeholder="Enter your complete address"
                value={formData.fullAddress}
                onChange={handleInputChange}
                required
                className="form-textarea"
                rows={3}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="landmark">Landmark (Optional)</label>
              <input
                type="text"
                id="landmark"
                name="landmark"
                placeholder="e.g., Near City Park, Opposite Gas Station"
                value={formData.landmark}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>
            
            <div className="form-check">
              <input
                type="checkbox"
                id="isDefault"
                name="isDefault"
                checked={formData.isDefault}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <label htmlFor="isDefault">Set as default address</label>
            </div>
            
            <div className="form-actions">
              <button type="submit" className="btn-save">
                {isEditing ? 'Update Address' : 'Save Address'}
              </button>
              <button type="button" className="btn-cancel" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AddressManager; 